/**
 * M1-7 / M12: Info Pill — bottom-right of the HUD (vertical pill).
 *
 * Buttons:
 *   1. VR Mode — delegates to renderer controller; WebGL 时弹出档位浮层
 *   2. Scene Info — opens SceneInfoPanel overlay (M1-7)
 *
 * Built on @heroui/react Button + @gravity-ui/icons.
 */
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewerStore } from '@stores/viewerStore';
import { useXrSupported } from '@hooks/useXrSupported';
import { useShareAction } from '@hooks/useShareAction';
import { IcXr, IcShare } from './HudIcons';
import { Button, ButtonGroup } from '@heroui/react';
import { Pill } from './PillPrimitives';
import type { VrQualityPreset } from '@mindcloudxai_3d/core';

const VR_PRESETS: { key: VrQualityPreset; labelKey: string; splatLabel: string }[] = [
  { key: 'lowest', labelKey: 'toolbar.vrQualityLowest', splatLabel: '300K' },
  { key: 'low', labelKey: 'toolbar.vrQualityLow', splatLabel: '500K' },
  { key: 'medium', labelKey: 'toolbar.vrQualityMedium', splatLabel: '800K' },
  { key: 'high', labelKey: 'toolbar.vrQualityHigh', splatLabel: '1.2M' },
  { key: 'higher', labelKey: 'toolbar.vrQualityHigher', splatLabel: '1.5M' },
  { key: 'highest', labelKey: 'toolbar.vrQualityHighest', splatLabel: '2M' },
];

interface InfoPillProps {
  onEnterVr?: () => boolean;
  onSelectVrQuality?: (preset: VrQualityPreset) => void;
}

export function InfoPill({ onEnterVr, onSelectVrQuality }: InfoPillProps) {
  const { t } = useTranslation();
  const xrSupported = useXrSupported();
  const sharePanelOpen = useViewerStore((s) => s.sharePanelOpen);
  const handleShare = useShareAction();
  const [vrPopoverOpen, setVrPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭浮层
  useEffect(() => {
    if (!vrPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setVrPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [vrPopoverOpen]);

  const handleVrClick = () => {
    const ready = onEnterVr?.();
    // onEnterVr 返回 true 表示已处于 WebGL，弹出档位选择
    if (ready) setVrPopoverOpen((v) => !v);
  };

  const handleSelectPreset = (preset: VrQualityPreset) => {
    setVrPopoverOpen(false);
    onSelectVrQuality?.(preset);
  };

  return (
    <div ref={containerRef} className="relative">
      <Pill horizontal={false} className="py-1">
        <ButtonGroup
          orientation="vertical"
          size="md"
          className="flex-col gap-0"
          variant="secondary"
        >
          {/* XR 按钮仅在支持 WebXR 的设备显示 */}
          {xrSupported && (
            <Button
              isIconOnly
              aria-label={t('toolbar.vrMode')}
              onPress={handleVrClick}
              className="rounded-t-full"
            >
              <IcXr className="size-4" />
            </Button>
          )}

          <Button
            isIconOnly
            aria-label={sharePanelOpen ? t('share.close-share') : t('share.share')}
            onPress={handleShare}
            className={xrSupported ? 'rounded-b-full' : 'rounded-full'}
          >
            {xrSupported && <ButtonGroup.Separator />}
            <IcShare className="size-4" />
          </Button>
        </ButtonGroup>
      </Pill>

      {/* VR 档位浮层：定位在 pill 上方 */}
      {vrPopoverOpen && (
        <div className="pointer-events-auto absolute right-0 bottom-full mb-2 w-44 rounded-2xl border border-white/20 bg-slate-950/95 p-3 shadow-2xl backdrop-blur-sm">
          <div className="mb-2 text-center text-xs font-medium text-white/70">
            {t('toolbar.vrQualityTitle')}
          </div>
          <div className="flex flex-col gap-1">
            {VR_PRESETS.map((preset) => (
              <button
                key={preset.key}
                onClick={() => handleSelectPreset(preset.key)}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors hover:bg-white/10"
              >
                <span className="font-medium text-white">{t(preset.labelKey)}</span>
                <span className="text-white/50">{preset.splatLabel}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
